import React, { useCallback, useEffect } from 'react';
import NumberFormat from 'react-number-format';

import { useFeatureToggle } from '@flopflip/react-redux';
import {
  getBooleanField,
  getInputMaxValueErrorText,
  getInputMinValueErrorText,
  getIntegerField,
  getTextField,
} from '@lender/utils';
import { Box, Typography } from '@mui/material';
import { CheckboxCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';
import { object } from 'yup';

import { FeatureFlag } from 'constants/featureFlags';
import { FormDateFormat, FormSchemaType } from 'constants/forms';
import { MAX_INTEGER_VALUE } from 'constants/number';
import useForm from 'hooks/useForm';
import { getConditionalObject } from 'utils/data';
import { getActionProperty, isPdfFormType } from 'utils/forms';

import { formsTestIds } from '../../forms.testIds';

import DynamicFieldBoldCheckbox from './DynamicFieldBoldCheckbox';
import { fullWidthCellStyles, sectionHeaderStyles } from './DynamicFieldOptions.styles';
import {
  getInitialHorizontalAlignment,
  getInitialVerticalAlignment,
} from './DynamicFieldOptions.utils';
import DynamicFieldOptionsLayout from './DynamicFieldOptionsLayout';
import StyleViewOptions from './StyleViewOptions';

const getSchema = ({ type }) => {
  const minAddDays = type === FormSchemaType.TODAY_X_DAYS ? 1 : 0;

  return object().shape({
    isBold: getBooleanField({ isRequired: false }),
    isUppercase: getBooleanField({ isRequired: false }),
    fontSize: getTextField({ isRequired: false }),
    horizontalAlignment: getTextField({ isRequired: false }),
    verticalAlignment: getTextField({ isRequired: false }),
    fontColor: getTextField({ isRequired: false }).nullable(),
    fontFamily: getTextField({ isRequired: false }),
    addDays: getIntegerField()
      .min(minAddDays, getInputMinValueErrorText(minAddDays))
      .max(MAX_INTEGER_VALUE, getInputMaxValueErrorText(MAX_INTEGER_VALUE)),
  });
};

const DynamicFieldTodaysDatePlusNumberView = ({
  setParentView,
  chosenOption,
  action,
  editor,
  handleClose,
  onSave,
  style,
  isNewItem = false,
}) => {
  const [title, value, type, label] = chosenOption;
  const isPdfForm = isPdfFormType(editor.formType);
  const isBackgroundColorEnabled = useFeatureToggle(FeatureFlag.EnableDynamicFieldBackgroundColor);

  const isDateOnlyInitialValue = action?.args?.[1] === FormDateFormat.DATE_ONLY ? true : false;

  const {
    form,
    setSchema,
    setInForm,
    validateField,
    handleChange,
    validationErrors,
    validateForm,
    isFormChanged,
  } = useForm({
    addDays: action?.args?.[0] || 0,
    isBold: !!style?.isBold,
    isUppercase: !!style?.isUppercase,
    horizontalAlignment: isPdfForm
      ? getInitialHorizontalAlignment({ align: style?.align, isNewItem })
      : undefined,
    verticalAlignment: isPdfForm
      ? getInitialVerticalAlignment({ verticalAlign: style?.verticalAlign })
      : undefined,
    fontColor: style?.fontColor,
    backgroundColor: isPdfForm ? style?.backgroundColor || null : null,
    fontSize: { value: style?.fontSize },
    fontFamily: { value: style?.fontFamily },
    isDateOnly: isDateOnlyInitialValue,
  });

  useEffect(() => {
    setSchema(getSchema({ type }));
  }, [setSchema, type]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      // The default day count is a valid choice, so an untouched dialog must still be validated.
      if (!(await validateForm({ noFormChangedValidate: true }))) {
        return;
      }

      const action = getActionProperty({
        addDays: Number(form.addDays),
        dataDynamicType: type,
        isDateOnly: form.isDateOnly,
      });

      onSave({
        title,
        value,
        type,
        label,
        action,
        style: {
          fontColor: form.fontColor,
          isBold: form.isBold,
          isUppercase: form.isUppercase,
          align: form.horizontalAlignment?.value,
          verticalAlign: form.verticalAlignment?.value,
          fontSize: form.fontSize?.value,
          fontFamily: form.fontFamily?.value,
          ...getConditionalObject(isPdfForm, { backgroundColor: form.backgroundColor || null }),
        },
        addDays: form.addDays,
      });
    },
    [validateForm, form, type, onSave, title, value, label, isPdfForm],
  );

  return (
    <DynamicFieldOptionsLayout
      chosenOption={chosenOption}
      dataTestId={formsTestIds.DYNAMIC_FIELD_TODAYS_DATE_PLUS_NUMBER_VIEW}
      editor={editor}
      isSubmitDisabled={!isNewItem && !isFormChanged}
      setParentView={setParentView}
      onClose={handleClose}
      onSubmit={onSubmit}
    >
      <Box sx={fullWidthCellStyles}>
        <Typography color="text.secondary" sx={sectionHeaderStyles} variant="bold12">
          Display Options & Formatting
        </Typography>
      </Box>

      <NumberFormat
        fullWidth
        required
        customInput={TextFieldCDS}
        decimalSeparator={false}
        error={!!validationErrors.addDays}
        helperText={validationErrors.addDays}
        id="addDays"
        label="Specify number of days to add"
        name="addDays"
        type="text"
        value={form.addDays}
        variant="outlined"
        onChange={handleChange}
      />

      <CheckboxCDS
        fullWidth
        dataTestId={formsTestIds.TODAYS_DATE_DATE_ONLY_CHECKBOX}
        label="Date Only"
        name="isDateOnly"
        value={form.isDateOnly}
        onChange={handleChange}
      />

      <DynamicFieldBoldCheckbox value={form.isBold} onChange={handleChange} />

      <Box />

      <StyleViewOptions
        backgroundColor={form.backgroundColor}
        fontColor={form.fontColor}
        fontFamily={form.fontFamily}
        fontSize={form.fontSize}
        handleChange={handleChange}
        horizontalAlignment={form.horizontalAlignment}
        isBackgroundColorEnabled={isPdfForm && isBackgroundColorEnabled}
        isBold={form.isBold}
        setInForm={setInForm}
        validateField={validateField}
        verticalAlignment={form.verticalAlignment}
      />
    </DynamicFieldOptionsLayout>
  );
};

DynamicFieldTodaysDatePlusNumberView.propTypes = {
  setParentView: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  chosenOption: PropTypes.array.isRequired,
  editor: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  action: PropTypes.object,
  style: PropTypes.object,
  isNewItem: PropTypes.bool,
};

export default DynamicFieldTodaysDatePlusNumberView;
